import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useUserRoles } from './useUserRoles';
import { useAuthQuery } from './useAuthQuery';
import type { ProjectPermission } from '@/types/auth';

export const useProjectPermissions = (projectId: string) => {
  const { user } = useAuth();
  const { data: userRoles, isLoading: rolesLoading } = useUserRoles();

  // Create a stable key that includes role information
  const roleKey = userRoles?.map(role => role.role_code).sort().join(',') || 'no-roles';

  return useAuthQuery<ProjectPermission>({
    queryKey: ['project-permissions', projectId, roleKey],
    queryFn: async (): Promise<ProjectPermission> => {
      if (!user?.id || !projectId) {
        return { can_view: false, can_edit: false, can_delete: false };
      }

      // Admins: full access
      const isAdmin = userRoles?.some(role => role.role_code === 'ROLE_ADMIN');
      if (isAdmin) {
        return { can_view: true, can_edit: true, can_delete: true };
      }

      // Per-user explicit permissions
      const { data: pup, error: pupErr } = await supabase
        .from('project_user_permissions')
        .select('can_view, can_edit, can_delete')
        .eq('user_id', user.id)
        .eq('project_id', projectId)
        .maybeSingle();

      if (pupErr) {
        return { can_view: false, can_edit: false, can_delete: false };
      }

      return {
        can_view: Boolean(pup?.can_view),
        can_edit: Boolean(pup?.can_edit),
        can_delete: Boolean(pup?.can_delete),
      };
    },
    requiresAuth: true,
    enabled: !!projectId && !rolesLoading && userRoles !== undefined,
  });
};